import React, { useCallback, useEffect, useState } from 'react';
import { TenantAccountingSettings, UserResponse } from '../../../providers/User/User.model';
import EditCompany from '../../EditCompany/EditCompany';
import AuthService from '../../../providers/Auth/Auth.service';
import StringUtils from '../../../utils/stringUtils';
import { LoadLocation } from '../../../redux/api/load/load.model';
import BillFromPreview, { BillFromPreviewProps } from './Preview';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import { useGetCompanyByIdsMutation } from '../../../redux/api/company/companyApi';
import { COMPANY_TYPE, Company } from '../../../redux/api/company/company.model';
import Stack from '@mui/material/Stack';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwCompaniesModal from '../../Companies/CompaniesModal/TwCompaniesModal';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TwCustomerMenu from '../../../components/TwCustomerMenu/TwCustomerMenu';
import TwEditCompaniesModal from '../../Companies/EditCompaniesModal/TwEditCompaniesModal';
import { CompaniesEntities } from '../../Companies/Companies';
import { CarrierResponse } from '../../../redux/api/carriers/carriers.model';

export interface BillFromData {
  placeId?: string;
  addressLine?: string;
  city?: string;
  state?: string;
  zip?: string;
  placeName?: string;
  companyName: string;
  companyPhone?: string;
}

export interface BillFromProps {
  onBillFromEdited?: (billData: BillFromData) => void;
  onNewLocation?: (location: LoadLocation) => void;
  companyIds?: string[];
  selectedCompanyId?: string;
  onSelectedCompanyChanged?: (companyId: string | undefined) => void;
  isBillFromChanged?: boolean;
  saveBillFrom: () => void;
  discardBillFrom: () => void;
  updateCompanyIds?: (companyIds: string[], selectedCompanyId?: string) => void;
  carriers: CarrierResponse[];
  tenantAccountingSettings?: TenantAccountingSettings;
}

// get company ids for carriers, divisions and populate the autocomplete

const BillFrom: React.FC<BillFromProps> = ({
  onBillFromEdited,
  companyIds,
  selectedCompanyId,
  onSelectedCompanyChanged,
  isBillFromChanged,
  saveBillFrom,
  discardBillFrom,
  updateCompanyIds,
  carriers,
  tenantAccountingSettings
}): JSX.Element => {
  const [tenantCompanyData, setTenantCompanyData] = useState<UserResponse | undefined>();
  const [billFromCompanies, setBillFromCompanies] = useState<Company[] | undefined>();
  const [companyOptions, setCompanyOptions] = useState<TwAutocompleteOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<TwAutocompleteOption | null>(null);
  const [isSelectedCompanyTenant, setIsSelectedCompanyTenant] = useState(false);
  const [previewData, setPreviewData] = useState<BillFromPreviewProps>();
  const [isBillFromDropdownOpen, setIsBillFromDropdownOpen] = useState<boolean>(false);
  const [selectedEntityType, setSelectedEntityType] = useState<CompaniesEntities>();
  const [selectedEntityId, setSelectedEntityId] = useState<string>();

  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Edit company dialog
  const [isEditCompanyModalOpen, setIsEditCompanyModalOpen] = useState(false);
  const [isEditTenantOpen, setEditTenantOpen] = useState(false);

  const [getCompanyByIds] = useGetCompanyByIdsMutation();

  /** * Gets account data */
  const getAccountInfo = useCallback(async () => {
    try {
      const userData = await AuthService.getCurrentUser();
      setTenantCompanyData(userData);
    } catch (error) {
      console.error("Couldn't get company info", error);
    }
  }, []);

  // get company data for bill from companies
  useEffect(() => {
    if (companyIds && companyIds.length > 0) {
      getCompanyByIds({ ids: companyIds })
        .unwrap()
        .then((comps) => {
          setBillFromCompanies(comps);
        })
        .catch((err) => {
          console.log({ err });
        });
    }
  }, [JSON.stringify(companyIds)]);

  const setSelectedData = (updatedBillFromCompanies: Company[] | undefined): void => {
    if (selectedCompanyId && updatedBillFromCompanies) {
      if (updatedBillFromCompanies) {
        const selectedCompany = updatedBillFromCompanies.find((c) => c.id === selectedCompanyId);
        if (selectedCompany) {
          setSelectedCompany({
            id: selectedCompany.id,
            label: selectedCompany.name,
            subLabel: selectedCompany.company_type,
            value: selectedCompany
          });
          setIsSelectedCompanyTenant(false);
          setPreviewData({
            name: selectedCompany.name,
            location: selectedCompany.location || undefined,
            billingEmail:
              selectedCompany.tenant_accounting_settings?.accounting_email ||
              selectedCompany.email_address,
            phone: selectedCompany.phone
          });

          if (selectedCompany.company_type === COMPANY_TYPE.CARRIER) {
            setSelectedEntityType(CompaniesEntities.carrier);
            const selectedCarrier = carriers.find((c) => c.company_id === selectedCompanyId);
            setSelectedEntityId(selectedCarrier?.id || '');
          } else {
            setSelectedEntityType(CompaniesEntities.division);
            setSelectedEntityId(selectedCompany.id);
          }
          return;
        }
      }
    } else {
      setSelectedCompany(null);
      setPreviewData(undefined);
    }
  };

  useEffect(() => {
    setSelectedData(billFromCompanies);
  }, [
    tenantCompanyData?.id,
    tenantCompanyData?.company_name,
    tenantCompanyData?.email_address,
    tenantCompanyData?.tenant?.location,
    tenantAccountingSettings?.accounting_email,
    JSON.stringify(billFromCompanies?.map((c) => c.id)),
    selectedCompanyId
  ]);

  useEffect(() => {
    (async (): Promise<void> => {
      await getAccountInfo();
    })();
  }, []);

  const handleCompanyEditOpen = (): void => {
    setIsBillFromDropdownOpen(true);
  };

  const handleCompanyEditClose = (): void => {
    setEditTenantOpen(false);
  };

  useEffect(() => {
    // If all data is present for the tenant company, we return the mandatory fields
    // to prevent errors in validations when invoice submits
    if (tenantCompanyData?.company_name) {
      const billData: BillFromData = {
        companyName: tenantCompanyData?.company_name
      };

      onBillFromEdited && onBillFromEdited(billData);
    }
  }, [tenantCompanyData]);

  // options

  const generateCompanyOptions = (): void => {
    // create options for autocomplete
    let compOptions: TwAutocompleteOption[] = [];
    if (billFromCompanies) {
      const options: TwAutocompleteOption[] = billFromCompanies.map((company) => {
        return {
          id: company.id,
          label: company.name,
          subLabel: company.is_default_division
            ? 'Your company'
            : StringUtils.capitalizeFirstLetter(company.company_type || ''),
          value: company
        };
      });
      compOptions = options;
    }

    setCompanyOptions(compOptions);
  };

  useEffect(() => {
    generateCompanyOptions();
  }, [
    tenantCompanyData?.id,
    tenantCompanyData?.company_name,
    tenantCompanyData?.email_address,
    tenantCompanyData?.tenant?.location,
    tenantAccountingSettings?.accounting_email,
    JSON.stringify(billFromCompanies?.map((c) => c.id))
  ]);

  const saveData = (): void => {
    setIsBillFromDropdownOpen(false);
    saveBillFrom();
  };

  const discardData = (): void => {
    setIsBillFromDropdownOpen(false);
    discardBillFrom();
  };

  const onClientCreateSuccess = (newClient: CarrierResponse): void => {
    const companyId = newClient.company_id ? newClient.company_id : newClient.id;
    updateCompanyIds &&
      companyId &&
      updateCompanyIds([...(companyIds || []), companyId], companyId);
  };

  const handleEditClientSuccess = async (
    clientResponse: CarrierResponse | Company
  ): Promise<void> => {
    // update current data in companies list
    setBillFromCompanies((companies) => {
      if (!companies) return companies;
      const currentCompanies = [...companies] || [];
      if (clientResponse) {
        if (selectedEntityType === CompaniesEntities.carrier) {
          const carrier = clientResponse as CarrierResponse;
          const index = currentCompanies?.findIndex((c) => c.id === carrier.company?.id);
          if (index !== undefined && index !== -1 && carrier.company) {
            currentCompanies[index] = carrier.company;
          }
        } else if (selectedEntityType === CompaniesEntities.division) {
          const company = clientResponse as Company;
          const index = currentCompanies?.findIndex((c) => c.id === company.id);
          if (index !== undefined && index !== -1) {
            currentCompanies[index] = company;
          }
        }
      }
      setSelectedData(currentCompanies);
      return currentCompanies;
    });
  };

  return (
    <div className="create-invoice__bill-from">
      <div className="create-invoice__bill-header">
        <span className="create-invoice__bill-title">Bill from</span>
        <span className="create-invoice__bill-edit-link" onClick={handleCompanyEditOpen}>
          Edit &apos;Bill from&apos; info
        </span>
      </div>

      {isBillFromDropdownOpen && (
        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0}>
          <TwAutocomplete
            id="bill-from-autocomplete"
            name="Company"
            label="Company"
            optionList={companyOptions}
            filterOptions={(x: any): any => x}
            value={selectedCompany}
            onChange={(_, newValue: TwAutocompleteOption | null): void => {
              let newCompId = undefined;
              if (newValue?.id !== tenantCompanyData?.id) {
                newCompId = newValue?.id;
              }
              onSelectedCompanyChanged && onSelectedCompanyChanged(newCompId);
            }}
            renderOption={(props, option): React.ReactNode => {
              return <RenderOption props={props} option={option} />;
            }}
            listAction={{
              label: 'New company or division',
              action: (): void => {
                setIsCompanyModalOpen(true);
              }
            }}
            disableNoOption
            enableClearIcon
          />

          <IconButton
            disableRipple={true}
            className="contact-details__menu-icon"
            onClick={(event: React.MouseEvent<HTMLElement>): void =>
              setAnchorEl(event.currentTarget)
            }
          >
            <MoreVertIcon />
          </IconButton>
          <TwCustomerMenu
            anchor={anchorEl}
            close={(): void => setAnchorEl(null)}
            menuOptions={[
              {
                label: 'Edit company',
                action: (): void => {
                  if (isSelectedCompanyTenant) {
                    setEditTenantOpen(true);
                  } else {
                    setIsEditCompanyModalOpen(true);
                  }
                },
                disabled: false
              }
            ]}
          />
        </Stack>
      )}

      <BillFromPreview {...previewData} />

      {isBillFromChanged && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          <TwButton type={TwButtonType.secondary} label="Discard" action={discardData} />
          <TwButton type={TwButtonType.primary} label="Save" action={saveData} />
        </Stack>
      )}

      <TwCompaniesModal
        open={isCompanyModalOpen}
        onClose={(): void => {
          setIsCompanyModalOpen(false);
        }}
        onSuccess={onClientCreateSuccess}
        disableCustomer
      />

      <EditCompany
        open={isEditTenantOpen}
        onClose={handleCompanyEditClose}
        onSuccess={(): void => {
          (async (): Promise<void> => {
            await getAccountInfo();
          })();
        }}
      />

      <TwEditCompaniesModal
        open={isEditCompanyModalOpen}
        onClose={(): void => {
          setIsEditCompanyModalOpen(false);
        }}
        entity={selectedEntityType || CompaniesEntities.carrier}
        entityId={selectedEntityId || ''}
        onSuccess={handleEditClientSuccess}
      />
    </div>
  );
};

export default BillFrom;
